// S2-FE2: QualityDimensionChart
// Uses Recharts — no separate CSS file. Colors reference token values directly.

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  Cell,
} from 'recharts';

export interface QualityDimension {
  id: string;
  name: string;
  score: number;
  threshold: number;
  pass: boolean;
}

export interface QualityDimensionChartProps {
  dimensions: QualityDimension[];
  threshold?: number;
}

// Token values used inline for Recharts (Recharts doesn't support var() in all places)
const COLORS = {
  pass: '#4A6FA5',       // --primary-500
  warn: '#F59E0B',       // --warning
  fail: '#EF4444',       // --error
  surface2: '#282624',   // --surface-2 / neutral-800
  border: '#1E1E1B',     // --border-default / neutral-850
  textMuted: '#78716C',  // --text-muted / neutral-500
};

// Determine bar color based on pass/fail and distance from threshold
function getBarColor(dim: QualityDimension, threshold: number): string {
  if (dim.pass) return COLORS.pass;
  if (dim.score >= threshold * 0.85) return COLORS.warn;
  return COLORS.fail;
}

// Custom tooltip to match dark theme
function ChartTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload: QualityDimension }> }) {
  if (!active || !payload?.length) return null;
  const dim = payload[0].payload;

  return (
    <div
      style={{
        background: COLORS.surface2,
        border: `1px solid ${COLORS.border}`,
        borderRadius: '8px',
        padding: '8px 12px',
        fontSize: '12px',
      }}
    >
      <div style={{ color: '#F5F4F2', fontWeight: 600 }}>{dim.name}</div>
      <div style={{ color: COLORS.textMuted, marginTop: '2px' }}>
        Score: {dim.score.toFixed(3)}
      </div>
    </div>
  );
}

export function QualityDimensionChart({
  dimensions,
  threshold = 0.85,
}: QualityDimensionChartProps) {
  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={dimensions} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
        <XAxis
          dataKey="id"
          tick={{ fontSize: 10, fill: COLORS.textMuted }}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          domain={[0, 1]}
          tick={{ fontSize: 10, fill: COLORS.textMuted }}
          tickLine={false}
          axisLine={false}
        />
        <Tooltip content={<ChartTooltip />} cursor={false} />
        <ReferenceLine
          y={threshold}
          stroke={COLORS.warn}
          strokeDasharray="4 4"
          strokeWidth={1}
        />
        <Bar dataKey="score" radius={[3, 3, 0, 0]} maxBarSize={32}>
          {dimensions.map((dim) => (
            <Cell key={dim.id} fill={getBarColor(dim, threshold)} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
